import React from 'react';
import { Card, SectionTitle, Row, StatBox, ProgressBar, Chip } from '../components/UI';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { CHART_DATA, ANNOUNCEMENTS } from '../data';

export default function DashboardScreen({ t, receipts, onNav }) {
  const hasNew = receipts.some(r => r.isNew);
  return (
    <div style={{ padding: '0 0 20px' }}>
      {/* Hero header */}
      <div style={{ background: t.headerBg, padding: '20px 20px 24px', marginBottom: -10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ color: t.isDark ? '#81C784' : '#A5D6A7', fontSize: 13, fontWeight: 600 }}>Good morning 👋</div>
            <div style={{ color: t.isDark ? '#E0E0E0' : '#fff', fontSize: 22, fontWeight: 900, marginTop: 2 }}>Rasel Ahmed</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              <div style={{ background: 'rgba(255,255,255,.15)', borderRadius: 7, padding: '3px 9px', fontSize: 11, color: '#fff', fontWeight: 700 }}>P02 · 2 shares</div>
              <div style={{ background: '#E3F2FD', borderRadius: 7, padding: '3px 9px', fontSize: 11, color: '#1565C0', fontWeight: 800 }}>Treasurer</div>
            </div>
          </div>
          <div style={{ position: 'relative', cursor: 'pointer' }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🔔</div>
            <div style={{ position: 'absolute', top: -2, right: -2, width: 16, height: 16, borderRadius: '50%', background: '#C62828', border: '2px solid #1B5E20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#fff', fontWeight: 900 }}>3</div>
          </div>
        </div>
      </div>

      <div style={{ padding: '18px 16px 0' }}>
        {/* New payment approved banner */}
        {hasNew && (
          <div onClick={() => onNav && onNav('receipts')} style={{ background: '#2E7D32', borderRadius: 12, padding: '12px 14px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}>
            <span style={{ fontSize: 20 }}>✅</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>Payment approved!</div>
              <div style={{ fontSize: 11, color: '#A5D6A7', fontWeight: 600, marginTop: 1 }}>Receipt auto-emailed · Tap to view</div>
            </div>
            <span style={{ color: '#A5D6A7', fontSize: 18 }}>→</span>
          </div>
        )}

        {/* Stats */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <StatBox t={t} value="96K" label="Installments" color="#1B5E20" />
          <StatBox t={t} value="13K" label="Down pay" color="#E65100" bg="#FFF3E0" border="#FFCC80" />
          <StatBox t={t} value="2sh" label="Shares" color="#1B5E20" />
        </div>

        {/* Deadline banner */}
        <div style={{ background: t.pending, borderRadius: 12, padding: '12px 14px', marginBottom: 14, border: `0.5px solid ${t.pendingBorder}`, display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 20 }}>⏰</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: t.pendingText }}>Deadline: 30 June · 5 days left</div>
            <div style={{ fontSize: 11, color: t.textSec, fontWeight: 600, marginTop: 1 }}>2 unpaid months · Suggested: 16,000 BDT</div>
          </div>
          <div onClick={() => onNav && onNav('pay')} style={{ background: t.pendingText, color: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 800, cursor: 'pointer', flexShrink: 0 }}>Pay now</div>
        </div>

        {/* Down payment progress */}
        <Card t={t} style={{ marginBottom: 14 }}>
          <SectionTitle t={t} label="Down payment — P02" />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
            <span style={{ color: t.textSec }}>Paid: 13,000 BDT</span>
            <span style={{ color: t.amber }}>54% of target</span>
          </div>
          <ProgressBar t={t} value={54} max={100} color="#E65100" />
          <div style={{ fontSize: 11, color: t.textHint, marginTop: 4, fontWeight: 500 }}>Target: 24,000 BDT (2 shares × 12,000)</div>
        </Card>

        {/* Monthly chart */}
        <Card t={t} style={{ marginBottom: 14 }}>
          <SectionTitle t={t} label="Monthly payments — P02 (2025)" />
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={CHART_DATA} barSize={24}>
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: t.textHint, fontFamily: 'Nunito' }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip formatter={(v) => [`${(v/1000).toFixed(0)}K`, 'Amount']} contentStyle={{ background: t.surface, border: `0.5px solid ${t.border}`, borderRadius: 8, fontSize: 11, fontFamily: 'Nunito' }} />
              <Bar dataKey="amount" fill={t.primary2} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Announcements */}
        <Card t={t} style={{ marginBottom: 14 }}>
          <SectionTitle t={t} label="Announcements" right={<span style={{ fontSize: 12, color: t.textHint, cursor: 'pointer', fontWeight: 600 }}>See all</span>} />
          {ANNOUNCEMENTS.map(ann => (
            <div key={ann.id} style={{ background: t.primaryLight, borderRadius: 10, padding: '10px 12px', marginBottom: 8, border: `0.5px solid ${t.borderLight}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 5 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: t.text, flex: 1 }}>{ann.title}</div>
                <div style={{ fontSize: 10, color: t.textHint, fontWeight: 600, flexShrink: 0, marginLeft: 8 }}>{ann.date}</div>
              </div>
              <div style={{ fontSize: 12, color: t.textSec, fontWeight: 500, lineHeight: 1.5, marginBottom: 8 }}>{ann.body}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {Object.entries(ann.reactions).map(([emoji, count]) => (
                  <div key={emoji} style={{ background: t.isDark ? '#1a3a1a' : t.primary, borderRadius: 16, padding: '3px 10px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <span>{emoji}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>{count}</span>
                  </div>
                ))}
              </div>
              {ann.requireAck && (
                <div style={{ background: ann.acknowledged ? t.primaryLight : '#FFF8E1', borderRadius: 8, padding: '8px 10px', marginTop: 8, border: `0.5px solid ${ann.acknowledged ? t.borderLight : '#FFE082'}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 20, height: 20, borderRadius: 5, background: ann.acknowledged ? t.primary : '#fff', border: `2px solid ${ann.acknowledged ? t.primary : '#FFE082'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#fff', flexShrink: 0 }}>{ann.acknowledged ? '✓' : ''}</div>
                  <span style={{ fontSize: 11, color: ann.acknowledged ? t.primary : '#795548', fontWeight: 600 }}>{ann.acknowledged ? 'Acknowledged on 25 Jun 2025' : 'Acknowledgment required — tap to confirm'}</span>
                </div>
              )}
            </div>
          ))}
        </Card>

        {/* Recent activity */}
        <Card t={t}>
          <SectionTitle t={t} label="Recent activity" />
          <Row t={t} label="17,500 approved · Jul+Aug 2025" value="✓ Paid" valueColor={t.primary} />
          <Row t={t} label="4,000 pending · Jun 2025" value="⏳ Review" valueColor={t.pendingText} />
          <Row t={t} last label="Jun reminder acknowledged" value="✓ Done" valueColor={t.primary} />
        </Card>
      </div>
    </div>
  );
}
